/**
 * Mock Face Verification Component
 *
 * A simple 2-page mock flow for testing face verification SDK integration.
 * - Page 1: Start verification button
 * - Page 2: Mock scan complete with success/failure options
 *
 * Uses postMessage protocol identical to real SDK for seamless testing.
 */

import { useState, useEffect } from "react";
import { logger } from "../../shared/utils/logger";
import { resolveParentOrigin } from "../../shared/utils/parentOrigin";

type Page = "page1" | "page2";

export function FaceVerificationMock() {
  const [page, setPage] = useState<Page>("page1");
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Resolve against the IDMe allowlist, same as the real SDK — never post a
      // verification result to an origin we don't recognise.
      const origin = resolveParentOrigin();
      if (!origin) {
        logger.log("[Mock SDK] Standalone mode - no allowlisted parent origin");
        return;
      }

      setParentOrigin(origin);

      // Signal to parent that we're ready
      window.parent.postMessage(
        {
          source: "idme-verification",
          type: "IDME_READY",
        },
        origin
      );

      logger.log("[Mock SDK] IDME_READY sent to parent, origin:", origin);

      // Listen for IDME_INIT from parent
      const handleInitMessage = (event: MessageEvent) => {
        if (event.data?.type !== "IDME_INIT") {
          return;
        }

        if (event.origin !== origin) {
          logger.warn("[Mock SDK] Ignoring IDME_INIT from unexpected origin:", event.origin);
          return;
        }

        logger.log("[Mock SDK] Received IDME_INIT:", event.data.payload);
        // Just log it - we're ready to proceed to page 1
        window.removeEventListener("message", handleInitMessage);
      };

      window.addEventListener("message", handleInitMessage);

      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (error) {
      logger.error("[Mock SDK] Initialization error:", error);
    }
  }, []);

  const handleStartVerification = () => {
    logger.log("[Mock SDK] Starting verification - moving to page 2");
    setPage("page2");
  };

  const handleCompleteVerification = () => {
    logger.log("[Mock SDK] Sending VERIFICATION_COMPLETE");

    if (parentOrigin) {
      window.parent.postMessage(
        {
          source: "idme-verification",
          type: "VERIFICATION_COMPLETE",
          payload: {
            data: {
              uuid: "mock-face-verification-uuid",
              type: "FACE_VERIFICATION",
              status: "completed",
              outcome: "passed",
              results: [],
            },
          },
        },
        parentOrigin
      );
    }

    // Close the window/iframe after a brief delay
    setTimeout(() => {
      window.close();
    }, 100);
  };

  const handleFailVerification = () => {
    logger.log("[Mock SDK] Sending VERIFICATION_ERROR");

    if (parentOrigin) {
      window.parent.postMessage(
        {
          source: "idme-verification",
          type: "VERIFICATION_ERROR",
          payload: {
            errorCode: "face_liveness_failed",
            message: "Mock face verification failed",
            feedback: "mock_failure",
            data: {},
          },
        },
        parentOrigin
      );
    }

    // Close the window/iframe after a brief delay
    setTimeout(() => {
      window.close();
    }, 100);
  };

  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f9f9f9",
    fontFamily: '"Open Sans", Arial, sans-serif',
    padding: "20px",
  };

  const contentStyle: React.CSSProperties = {
    textAlign: "center",
    maxWidth: "400px",
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: 600,
    color: "#2e3f51",
    margin: "0 0 12px 0",
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#666",
    margin: "0 0 32px 0",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: 600,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginTop: "16px",
    width: "100%",
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "#0050C7",
    color: "white",
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "transparent",
    color: "#0050C7",
    border: "1px solid #0050C7",
  };

  const checkmarkStyle: React.CSSProperties = {
    fontSize: "60px",
    color: "#0050C7",
    margin: "0 0 24px 0",
  };

  if (page === "page1") {
    return (
      <div style={containerStyle}>
        <div style={contentStyle}>
          <h1 style={headingStyle}>Mock Face Verification</h1>
          <p style={subtitleStyle}>This is a test environment</p>
          <button style={primaryButtonStyle} onClick={handleStartVerification}>
            Start verification
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <h1 style={headingStyle}>Mock Scan Complete</h1>
        <div style={checkmarkStyle}>✓</div>
        <button style={primaryButtonStyle} onClick={handleCompleteVerification}>
          Complete verification
        </button>
        <button style={secondaryButtonStyle} onClick={handleFailVerification}>
          Fail verification
        </button>
      </div>
    </div>
  );
}
